/**
 * Every time this dashboard shows is Melbourne time, because that is where the
 * bets are placed from.
 *
 * Timestamps arrive from the API as UTC instants, which is the right way to
 * store them - UTC is unambiguous and never shifts under daylight saving. It
 * is the wrong thing to *read*. The ledger used to print the raw instant with
 * a "UTC" suffix, so a bet placed at 9:06am on Tuesday read as 23:06 Monday,
 * and the daily profit chart dated it to Monday as well: anything placed in
 * the morning window landed on the previous day's bar.
 *
 * So instants are stored in UTC and displayed here, and only here, in
 * Melbourne.
 */
const MELBOURNE = "Australia/Melbourne";

/**
 * The Melbourne calendar date an instant falls on, as YYYY-MM-DD.
 *
 * en-CA is not a display choice - it is the locale whose short date format is
 * already ISO, which avoids reassembling the parts by hand.
 */
export function melbourneDay(at: string | Date): string {
  const parsed = at instanceof Date ? at : new Date(at);
  if (Number.isNaN(parsed.getTime()))
    return typeof at === "string" ? at.slice(0, 10) : "";
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: MELBOURNE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(parsed);
}

/** Date and time, for a row that has room for both. */
export function melbourneStamp(at: string | Date): string {
  const parsed = at instanceof Date ? at : new Date(at);
  if (Number.isNaN(parsed.getTime())) return typeof at === "string" ? at : "";
  return new Intl.DateTimeFormat("en-AU", {
    timeZone: MELBOURNE,
    day: "numeric",
    month: "short",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  }).format(parsed);
}

/** "Thu 18 Sep", the day a match is on in Melbourne. */
export function melbourneWeekday(at: string | Date): string {
  const parsed = at instanceof Date ? at : new Date(at);
  if (Number.isNaN(parsed.getTime())) return "";
  return new Intl.DateTimeFormat("en-AU", {
    timeZone: MELBOURNE,
    weekday: "short",
    day: "numeric",
    month: "short",
  }).format(parsed);
}

/**
 * How long until kickoff, coarse enough to read at a glance: "in 2d 4h",
 * "in 5h", "in 40m", "started".
 */
export function untilStart(at: string | Date, now: Date = new Date()): string {
  const parsed = at instanceof Date ? at : new Date(at);
  const minutes = Math.round((parsed.getTime() - now.getTime()) / 60_000);
  if (!Number.isFinite(minutes)) return "";
  if (minutes <= 0) return "started";
  if (minutes < 60) return `in ${String(minutes)}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `in ${String(hours)}h`;
  return `in ${String(Math.floor(hours / 24))}d ${String(hours % 24)}h`;
}

/** Clock time alone, for a row that has already said which day it means. */
export function melbourneClock(at: string | Date): string {
  const parsed = at instanceof Date ? at : new Date(at);
  if (Number.isNaN(parsed.getTime())) return typeof at === "string" ? at : "";
  return new Intl.DateTimeFormat("en-AU", {
    timeZone: MELBOURNE,
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  }).format(parsed);
}
